import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { useLockBodyScroll } from '@as/cotopaxi';
import { Box, Breakpoint, Carousel, CarouselContent, CarouselNext, CarouselPrevious, CarouselSlot, Grid, isScreenDesktop, Stack, useDialogContext, useIsClient, useMediaQueryMatchesOnDesktop, } from '@yonderland/andes-react';
import { useCallback, useEffect, useState } from 'react';
import { ProductMediaItem } from '../ProductMediaItem';
import { useProductMediaLightBox } from './ProductMediaLightBoxContext';
import { ProductMediaLightBoxThumbs, THUMB_SLIDE_HEIGHT } from './ProductMediaLightBoxThumbs';
const MODAL_HEADER_HEIGHT_DESKTOP = 72 + 40; // 72px header + 40px gap
const MODAL_HEADER_HEIGHT_MOBILE = 72 + 70; // 72px header + 70px controls
const getSlideHeight = (isDesktop) => `calc(100dvh - ${isDesktop ? THUMB_SLIDE_HEIGHT + MODAL_HEADER_HEIGHT_DESKTOP : MODAL_HEADER_HEIGHT_MOBILE}px)`;
export const ProductPreviewLightBoxMain = () => {
    const isClient = useIsClient();
    const { startIndex, alt, mediaCollection } = useProductMediaLightBox();
    const { isOpen } = useDialogContext();
    const isDesktop = useMediaQueryMatchesOnDesktop({ isSsr: !isClient });
    useLockBodyScroll(isOpen);
    const [selectedIndex, setSelectedIndex] = useState(startIndex);
    const [mainApi, setMainApi] = useState(null);
    const [thumbApi, setThumbApi] = useState(null);
    const [slidesInView, setSlidesInView] = useState([]);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const handleThumbClick = (index) => {
        if (!mainApi || !thumbApi)
            return;
        mainApi.scrollTo(index);
        setCanScrollNext(mainApi.canScrollNext());
        setCanScrollPrev(mainApi.canScrollPrev());
    };
    const onSelect = useCallback(() => {
        if (!mainApi || !thumbApi)
            return;
        setSelectedIndex(mainApi.selectedScrollSnap());
        setCanScrollNext(mainApi.canScrollNext());
        setCanScrollPrev(mainApi.canScrollPrev());
        thumbApi.scrollTo(mainApi.selectedScrollSnap());
    }, [mainApi, thumbApi]);
    useEffect(() => {
        if (!mainApi)
            return;
        onSelect();
        mainApi.on('select', onSelect).on('reInit', onSelect);
        return () => {
            mainApi.off('select', onSelect).off('reInit', onSelect);
        };
    }, [mainApi, onSelect]);
    const updateSlidesInView = useCallback((emblaApi) => {
        if (!emblaApi)
            return;
        setSlidesInView(slidesInView => {
            if (slidesInView.length === emblaApi.slideNodes().length) {
                emblaApi.off('slidesInView', updateSlidesInView);
            }
            const inView = emblaApi.slidesInView().filter((index) => !slidesInView.includes(index));
            return slidesInView.concat(inView);
        });
    }, []);
    useEffect(() => {
        if (!mainApi)
            return;
        updateSlidesInView(mainApi);
        mainApi.on('slidesInView', updateSlidesInView);
        mainApi.on('reInit', updateSlidesInView);
    }, [mainApi, updateSlidesInView]);
    const renderSlide = useCallback((item, index) => (_jsx(CarouselSlot, { children: _jsx(Box, { display: item.isVideo ? 'block' : 'flex', backgroundColor: "quiet-3", aspectRatio: "2/3", contentAlign: "center", children: item.isVideo ? (_jsx(Grid, { fullWidth: true, children: _createElement(ProductMediaItem, Object.assign({}, item, { key: index, alt: alt, stop: !isOpen })) })) : (_createElement(ProductMediaItem, Object.assign({}, item, { key: index, slideInView: slidesInView.indexOf(index) > -1, alt: alt }))) }) }, `lb_image_${index}`)), [selectedIndex, startIndex, isClient, slidesInView, isOpen]);
    return (_jsx(Carousel, { testId: "light_box_main_slider", slideSize: "auto", slideHeight: getSlideHeight(isClient && isScreenDesktop()), hideOverflow: true, opts: {
            align: 'center',
            startIndex,
            slidesToScroll: 1,
            loop: true,
        }, setApi: setMainApi, gap: 2, children: _jsxs(Stack, { direction: "col", gap: 4, children: [_jsx(CarouselContent, { children: mediaCollection.map(renderSlide) }), _jsxs(Stack, { gap: 4, justify: isDesktop ? 'between' : 'end', align: "start", children: [_jsx(Breakpoint, { from: "lg", children: _jsx(Box, { overflow: "hidden", children: _jsx(ProductMediaLightBoxThumbs, { setApi: setThumbApi, selectedIndex: selectedIndex, onThumbClick: handleThumbClick, canScrollPrev: canScrollPrev, canScrollNext: canScrollNext }) }) }), _jsxs(Stack, { gap: 1, children: [_jsx(CarouselPrevious, {}), _jsx(CarouselNext, {})] })] })] }) }));
};
